import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { isReturnableUrl } from './returnable-url';

/**
 * Remembers the last page the user was on outside of /settings, so the settings
 * "back" button returns them there instead of a hardcoded destination.
 *
 * Falls back to the orders list when there is nothing to go back to — a deep link
 * or a browser refresh straight into /settings, for example.
 *
 * Must be constructed at app start to see navigations that happen before the
 * settings sidebar exists; AppComponent injects it for exactly that reason.
 */
@Injectable({ providedIn: 'root' })
export class SettingsReturnUrlService {
  /** Orders list — see CLAUDE.md: the orders table is the quotations component. */
  static readonly FALLBACK_URL = '/quotations';

  /**
   * On top of the shared unauthenticated routes in `returnable-url`, the
   * settings section itself is not a "back" target — it is where the button
   * lives.
   */
  private static readonly EXCLUDED_PREFIXES = ['/settings'];

  private lastExternalUrl: string | null = null;

  constructor(private router: Router) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        if (isReturnableUrl(url, SettingsReturnUrlService.EXCLUDED_PREFIXES)) {
          this.lastExternalUrl = url;
        }
      });
  }

  /** Where the settings back button should navigate. */
  get returnUrl(): string {
    return this.lastExternalUrl ?? SettingsReturnUrlService.FALLBACK_URL;
  }
}
